import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import { useSettingsObject } from '../../../settings/hooks';
import { getUserThemeColor, toRgba } from '../../../../ui/theme/colors';
import Icon from '../../../../ui/Icon';
import coursioNetworkLogo from '../../../../ui/assets/coursio-logo.svg';

const CTAContainer = styled(Flex)`
  background: linear-gradient(
    135deg,
    ${(props) => props.$brandColor || theme.colors.PRIMARY}08 0%,
    ${theme.colors.SECONDARY}08 50%,
    ${theme.colors.MARKER}08 100%
  );
  border-radius: 16px;
  padding: 32px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px ${toRgba(theme.colors.NEU_999, 0.08)};
  border: 1px solid ${theme.colors.NEU_200};

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -10%;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    z-index: 0;
  }
`;

const HeaderLogo = styled.img`
  height: 80px;
  width: auto;
  object-fit: contain;
  margin-bottom: 16px;
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin: 24px 0;
  position: relative;
  z-index: 1;
`;

const BenefitItem = styled(Flex)`
  padding: 12px 16px;
  border-radius: 8px;
  background: ${theme.colors.NEU_0};
  border: 1px solid ${theme.colors.NEU_200};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.08)};
    border-color: ${(props) => props.$brandColor || theme.colors.PRIMARY};
  }
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 32px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${(props) => props.$brandColor || theme.colors.PRIMARY} 0%, ${theme.colors.SECONDARY} 100%);
  color: ${theme.colors.NEU_0};
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  box-shadow: 0 4px 16px ${(props) => props.$brandColor || theme.colors.PRIMARY}40;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, ${toRgba(theme.colors.NEU_0, 0.3)}, transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px ${(props) => props.$brandColor || theme.colors.PRIMARY}60;

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 32px;
  border-radius: 12px;
  background: ${theme.colors.NEU_0};
  color: ${theme.colors.NEU_PRIMARY};
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 2px solid ${theme.colors.NEU_200};

  &:hover {
    background: ${theme.colors.NEU_100};
    border-color: ${(props) => props.$brandColor || theme.colors.PRIMARY};
    color: ${(props) => props.$brandColor || theme.colors.PRIMARY};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid ${theme.colors.NEU_200};
  position: relative;
  z-index: 1;
`;

const StatItem = styled(Flex)`
  text-align: center;
  padding: 16px;
  border-radius: 12px;
  background: ${theme.colors.NEU_0};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px ${toRgba(theme.colors.NEU_999, 0.1)};
  }
`;

const CoursioNetworkCTA = () => {
  const settings = useSettingsObject();
  const userTheme = settings?.theme;
  const brandColor = userTheme?.color ? getUserThemeColor(userTheme?.color) : theme.colors.PRIMARY;

  const benefits = [
    { icon: 'Users', text: 'Access to 500+ resellers' },
    { icon: 'TrendingUp', text: 'Expand your reach 10x' },
    { icon: 'DollarSign', text: 'Earn passive income' },
    { icon: 'Certificate', text: 'Verified quality badge' },
  ];

  return (
    <CTAContainer direction='column' $brandColor={brandColor}>
      <Flex direction='column' align='center' gap='12'>
        <HeaderLogo src={coursioNetworkLogo} alt='Coursio Network' />
        <Typo.Text color={theme.colors.NEU_600}>List your courses with our network of trusted resellers and reach thousands of new students.</Typo.Text>
      </Flex>

      <BenefitsList $brandColor={brandColor}>
        {benefits.map((benefit, index) => {
          const BenefitIcon = Icon[benefit.icon];
          const iconColors = [brandColor, theme.colors.SECONDARY, theme.colors.MARKER, brandColor];
          return (
            <BenefitItem key={index} align='center' gap='10' $brandColor={brandColor}>
              {BenefitIcon && <BenefitIcon size='small' color={iconColors[index]} />}
              <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY}>
                {benefit.text}
              </Typo.MediumText>
            </BenefitItem>
          );
        })}
      </BenefitsList>

      <StatsGrid>
        <StatItem direction='column' gap='4'>
          <Typo.ContentBigHeader color={brandColor}>500+</Typo.ContentBigHeader>
          <Typo.SmallText color={theme.colors.NEU_600} fontWeight='500'>
            Active Resellers
          </Typo.SmallText>
        </StatItem>
        <StatItem direction='column' gap='4'>
          <Typo.ContentBigHeader color={theme.colors.SECONDARY}>50k+</Typo.ContentBigHeader>
          <Typo.SmallText color={theme.colors.NEU_600} fontWeight='500'>
            Students Reached
          </Typo.SmallText>
        </StatItem>
        <StatItem direction='column' gap='4'>
          <Typo.ContentBigHeader color={theme.colors.MARKER}>€2M+</Typo.ContentBigHeader>
          <Typo.SmallText color={theme.colors.NEU_600} fontWeight='500'>
            Revenue Generated
          </Typo.SmallText>
        </StatItem>
      </StatsGrid>

      <Flex gap='16' justify='center' style={{ marginTop: '28px' }}>
        <CTAButton href='https://coursio-web-2025.vercel.app/coursio-network/' target='_blank' rel='noopener noreferrer' $brandColor={brandColor}>
          Join the Network
          <Icon.ArrowRight size='x-small' />
        </CTAButton>
        <SecondaryButton href='https://coursio-web-2025.vercel.app/coursio-network/' target='_blank' rel='noopener noreferrer' $brandColor={brandColor}>
          Learn More
          <Icon.ExternalLink size='x-small' />
        </SecondaryButton>
      </Flex>
    </CTAContainer>
  );
};

export default CoursioNetworkCTA;
